import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Category, Calendar, Edit, Trash2, FileDown, ChevronDown, Clock, History, Plus, Copy } from 'lucide-react';
import {
  fetchProducts,
  deleteProductById,
  clearMessages,
} from '../../../redux/slices/productSlice';
import { fetchCategories } from '../../../redux/slices/categorySlice';
import { fetchInventoryLogs } from '../../../redux/slices/inventorySlice';
import toast from 'react-hot-toast';
import { TrendingUp, ShoppingBag, Users, User, Gift, DollarSign, FileSpreadsheet, FileText, Printer } from 'lucide-react';
import { getAllSales } from '../../../services/salesService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Products = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, loading: productLoading, error, success } = useSelector((state) => state.product);
  const { categories } = useSelector((state) => state.category);
  const { logs, loading: logsLoading } = useSelector((state) => state.inventory);

  const [sales, setSales] = useState([]);

  // Filter states
  const [rawMaterial, setRawMaterial] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [unitType, setUnitType] = useState('Purchase Unit');
  const [showSpecialOnly, setShowSpecialOnly] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];
  const currentYearMonth = todayStr.substring(0, 7);

  const [dateFilterType, setDateFilterType] = useState('custom'); // 'today', 'month', 'custom'
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Pagination
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch products and categories on component mount
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchCategories());
    dispatch(fetchInventoryLogs());

    // Fetch sales for Sales VP mapping
    (async () => {
      try {
        const s = await getAllSales();
        setSales(s);
      } catch (err) {
        console.error("Failed to fetch sales for VP summary", err);
      }
    })();
  }, [dispatch]);

  // Handle success and error messages
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearMessages());
    }
    if (success) {
      dispatch(clearMessages());
    }
  }, [error, success, dispatch]);

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await dispatch(deleteProductById(productId)).unwrap();
        toast.success('Product deleted successfully');
      } catch (error) {
        toast.error('Failed to delete product');
        console.error('Error:', error);
      }
    }
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'N/A';
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesRawMaterial = rawMaterial === '' ||
      product.name.toLowerCase().includes(rawMaterial.toLowerCase()) ||
      product.productCode.toLowerCase().includes(rawMaterial.toLowerCase());

    const matchesCategory = selectedCategory === 'All' ||
      getCategoryName(product.categoryId) === selectedCategory;

    const matchesSpecial = !showSpecialOnly || product.isSpecial;

    return matchesRawMaterial && matchesCategory && matchesSpecial;
  }).sort((a, b) => {
    // Show newly created products first 
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to page 1 whenever filters change
  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setRawMaterial('');
    setSelectedCategory('All');
    setUnitType('Purchase Unit');
    setShowSpecialOnly(false);
    setFromDate(todayStr);
    setToDate(todayStr);
    setSelectedMonth(currentYearMonth);
    setDateFilterType('custom');
  };

  // Handle search
  const handleSearch = () => {
    toast.success('Filters applied');
  };

  // Export handlers
  const handleExport = (format) => {
    const exportData = filteredProducts.map(product => ({
      'Product Code': product.productCode,
      'Name': product.name,
      'Opening (A)': product.stockCount || 0,
      'Purchase (B)': product.purchase || 0,
      'Club Used': product.clubUsed || 0,
      'Sale': product.sale || 0,
      'Personal Used': product.personalUsed || 0,
      'Gifted': product.gifted || 0,
      'Closing Stock': calculateClosingStock(product),
      'Purchase Price': product.purchasePrice || 0,
      'Order VP': calculateOrderVP(product),
      'Outward VP': calculateOutwardVP(product),
      'Available VP': calculateAvailableVP(product),
      'Status': (product.status === 'active' || product.isActive === true) ? 'Active' : 'Disabled'
    }));

    // Add totals row
    const totalRow = {
      'Product Code': 'TOTAL',
      'Name': '',
      'Opening (A)': exportData.reduce((sum, p) => sum + (Number(p['Opening (A)']) || 0), 0),
      'Purchase (B)': exportData.reduce((sum, p) => sum + (Number(p['Purchase (B)']) || 0), 0),
      'Club Used': exportData.reduce((sum, p) => sum + (Number(p['Club Used']) || 0), 0),
      'Sale': exportData.reduce((sum, p) => sum + (Number(p['Sale']) || 0), 0),
      'Personal Used': exportData.reduce((sum, p) => sum + (Number(p['Personal Used']) || 0), 0),
      'Gifted': exportData.reduce((sum, p) => sum + (Number(p['Gifted']) || 0), 0),
      'Closing Stock': exportData.reduce((sum, p) => sum + (Number(p['Closing Stock']) || 0), 0),
      'Purchase Price': '',
      'Order VP': exportData.reduce((sum, p) => sum + (Number(p['Order VP']) || 0), 0).toFixed(2),
      'Outward VP': exportData.reduce((sum, p) => sum + (Number(p['Outward VP']) || 0), 0).toFixed(2),
      'Available VP': exportData.reduce((sum, p) => sum + (Number(p['Available VP']) || 0), 0).toFixed(2),
      'Status': ''
    };
    exportData.push(totalRow);

    if (format === 'Excel' || format === 'CSV') {
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stock Summary");
      const fileName = `Stock_Summary_${todayStr}.${format === 'Excel' ? 'xlsx' : 'csv'}`;
      XLSX.writeFile(wb, fileName);
    } else if (format === 'PDF') {
      const doc = new jsPDF('landscape');
      doc.setFont("helvetica", "bold");
      doc.text("Stock Summary Report", 14, 15);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      let periodText = `Period: ${fromDate} to ${toDate}`;
      if (dateFilterType === 'today') periodText = `Period: Today (${todayStr})`;
      if (dateFilterType === 'month') periodText = `Period: Month (${selectedMonth})`;
      doc.text(periodText, 14, 22);

      const tableData = exportData.map(d => [
        d['Product Code'], d.Name, d['Opening (A)'], d['Purchase (B)'],
        d['Club Used'], d.Sale, d['Personal Used'], d.Gifted,
        d['Closing Stock'], d['Order VP'], d['Outward VP'], d['Available VP']
      ]);

      autoTable(doc, {
        head: [['Code', 'Product', 'Open', 'Purc', 'Club', 'Sale', 'Pers', 'Gift', 'Clos', 'OrdVP', 'OutVP', 'AvailVP']],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8, textColor: [40, 40, 40], font: "helvetica" },
        headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        footStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: "bold" }
      });

      doc.save(`Stock_Summary_${todayStr}.pdf`);
    } else if (format === 'Print') {
      window.print();
    }

    setShowExportMenu(false);
    toast.success(`Exporting as ${format}...`);
  };

  const handleScheduleReport = () => {
    toast.info('Schedule Report feature coming soon');
  };

  const calculateClosingStock = (product) => {
    const opening = Number(product.stockCount) || 0;
    const purchase = Number(product.purchase) || 0;
    const clubUsed = Number(product.clubUsed) || 0;
    const sale = Number(product.sale) || 0;
    const personalUsed = Number(product.personalUsed) || 0;
    const gifted = Number(product.gifted) || 0;

    const result = opening + purchase - (clubUsed + sale + personalUsed + gifted);

    console.log(`${product.name}: Opening=${opening}, Purchase=${purchase}, ClubUsed=${clubUsed}, Sale=${sale}, PersonalUsed=${personalUsed}, Closing=${result}`);

    return result;
  };

  // Calculate Order Value Point (Purchase × Product VP)
  const calculateOrderVP = (product) => {
    const purchase = Number(product.purchase || 0);
    const valuePoint = Number(product.volPoint || 0);
    return parseFloat((purchase * valuePoint).toFixed(2));
  };

  // Calculate Outward Value Point ((Club Used + Sale + Personal Used + Gifted) × Product VP)
  const calculateOutwardVP = (product) => {
    const clubUsed = Number(product.clubUsed || 0);
    const sale = Number(product.sale || 0);
    const personalUsed = Number(product.personalUsed || 0);
    const gifted = Number(product.gifted || 0);
    const valuePoint = Number(product.volPoint || 0);

    return parseFloat(((clubUsed + sale + personalUsed + gifted) * valuePoint).toFixed(2));
  };

  // Calculate Available Value Point (Closing Stock × Product VP)
  const calculateAvailableVP = (product) => {
    const closingStock = calculateClosingStock(product);
    const valuePoint = Number(product.volPoint || 0);
    return parseFloat((closingStock * valuePoint).toFixed(2));
  };

  // Calculate VP Summary based on inventoryLogs and date range
  // inventoryLogs shape: { productId, date, quantityChange (negative=outbound), transactionType, ... }
  const calculateVPSummary = () => {
    const summary = {
      purchase: 0,
      clubUsed: 0,
      sale: 0,
      personalUsed: 0,
      gifted: 0,
      totalOutward: 0
    };

    const filteredLogs = logs.filter(log => {
      const logDate = log.date; // already "YYYY-MM-DD" string
      if (dateFilterType === 'today') return logDate === todayStr;
      if (dateFilterType === 'month') return logDate.startsWith(selectedMonth);
      return logDate >= fromDate && logDate <= toDate;
    });

    filteredLogs.forEach(log => {
      const product = products.find(p => p.id === log.productId);
      if (!product) return;
      const vp = Number(product.volPoint || 0);
      const absQty = Math.abs(Number(log.quantityChange || 0));
      const totalVP = parseFloat((vp * absQty).toFixed(2));
      const txType = (log.transactionType || '').toLowerCase();

      if (log.quantityChange > 0) {
        // Inbound: Purchase Topup or Manual Adjustment (positive)
        summary.purchase += totalVP;
      } else {
        // Outbound: categorise by transactionType
        if (txType.includes('store sale')) {
          // SKIP: We will compute Sales VP from actual Sales Records directly
        } else if (txType.includes('guest shake') || txType.includes('associate shake') || txType.includes('recharge') || txType.includes('club')) {
          summary.clubUsed += totalVP;
          summary.totalOutward += totalVP;
        } else if (txType.includes('personal')) {
          summary.personalUsed += totalVP;
          summary.totalOutward += totalVP;
        } else if (txType.includes('gifted') || txType.includes('donation') || txType.includes('trial')) {
          summary.gifted += totalVP;
          summary.totalOutward += totalVP;
        } else {
          // fallback: count as outward
          summary.totalOutward += totalVP;
        }
      }
    });

    // Compute exact Sales VP from real sales independently
    const filteredSales = sales.filter(s => {
      const saleDate = s.date;
      if (dateFilterType === 'today') return saleDate === todayStr;
      if (dateFilterType === 'month') return saleDate?.startsWith(selectedMonth);
      return saleDate >= fromDate && saleDate <= toDate;
    });

    let realSalesVP = 0;
    filteredSales.forEach(s => {
      (s.items || []).forEach(item => {
        const product = products.find(p => p.id === item.productId || p.name === item.productName);
        const vp = product ? Number(product.volPoint || 0) : 0;
        realSalesVP += (vp * item.qty);
      });
    });

    summary.sale = parseFloat(realSalesVP.toFixed(2));
    summary.totalOutward += summary.sale;

    return summary;
  };

  const vpSummary = calculateVPSummary();
  const loading = productLoading || logsLoading;

  return (
    <div className='max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen'>
      {/* Header */}
      <div className='mb-4 md:mb-6 flex justify-between items-start'>
        <div>
          <h1 className='text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 md:mb-2'>
            Stock Summary Report
          </h1>
          <p className='text-[11px] md:text-sm text-gray-600'>Track inventory stock movements</p>
        </div>
        <div className='flex flex-col md:flex-row gap-2'>
          <div className='flex gap-2'>
            <button
              onClick={handleScheduleReport}
              className='flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-100 transition-all font-medium text-[11px] md:text-sm'
            >
              <Clock className='w-4 h-4' />
              <span className='hidden md:inline'>Schedule Report</span>
            </button>
            <div className='relative'>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className='flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all font-medium text-[11px] md:text-sm'
              >
                <FileDown className='w-4 h-4' />
                <span>Export</span>
                <ChevronDown className='w-4 h-4' />
              </button>
              {showExportMenu && (
                <div className='absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-xl shadow-lg z-10'>
                  <button
                    onClick={() => handleExport('Excel')}
                    className='w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 first:rounded-t-xl'
                  >
                    <FileSpreadsheet className='w-4 h-4 text-green-600' />
                    Excel Report
                  </button>
                  <button
                    onClick={() => handleExport('CSV')}
                    className='w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2'
                  >
                    <FileText className='w-4 h-4 text-blue-600' />
                    CSV File
                  </button>
                  <button
                    onClick={() => handleExport('PDF')}
                    className='w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2'
                  >
                    <FileDown className='w-4 h-4 text-red-600' />
                    PDF Document
                  </button>
                  <button
                    onClick={() => handleExport('Print')}
                    className='w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 last:rounded-b-xl'
                  >
                    <Printer className='w-4 h-4 text-gray-600' />
                    Print
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className='flex gap-2'>
            <button
              onClick={() => navigate('/admin/inventory-history')}
              className='flex-1 md:flex-none h-9 md:h-10 px-4 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium text-[11px] md:text-sm flex items-center justify-center gap-2 shadow-sm'
            >
              <History className='w-4 h-4' />
              <span>History</span>
            </button>
            <button
              onClick={() => navigate('/admin/product/add')}
              className='flex-1 md:flex-none h-9 md:h-10 px-4 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl transition-all shadow-lg shadow-neutral-900/10 flex items-center justify-center gap-2 text-[11px] md:text-sm font-medium'
            >
              <Plus className='w-4 h-4' />
              <span>Add Product</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 md:gap-4'>
          {/* Raw Material */}
          <div>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              Raw Material
            </label>
            <input
              type='text'
              value={rawMaterial}
              onChange={(e) => { setRawMaterial(e.target.value); setCurrentPage(1); }}
              placeholder='Search product...'
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
            />
          </div>

          {/* Category */}
          <div>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
            >
              <option value='All'>All</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Type UI */}
          <div className="col-span-1 md:col-span-2 lg:col-span-2 flex flex-col gap-2">
            <div className="flex gap-2">
              <label className='block text-[11px] md:text-xs font-medium text-gray-700 mt-1 mr-2'>
                Date Period:
              </label>
              <select
                value={dateFilterType}
                onChange={(e) => setDateFilterType(e.target.value)}
                className='px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-[13px] bg-gray-50'
              >
                <option value="today">Today</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Sub-inputs dependent on dateFilterType */}
            {dateFilterType === 'custom' && (
              <div className="flex gap-2 items-center w-full">
                <div className='relative flex-1'>
                  <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    type='date'
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className='w-full pl-9 pr-2 py-1.5 border border-gray-300 rounded-lg outline-none text-[12px]'
                  />
                </div>
                <span className="text-xs text-gray-400">to</span>
                <div className='relative flex-1'>
                  <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                  <input
                    type='date'
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className='w-full pl-9 pr-2 py-1.5 border border-gray-300 rounded-lg outline-none text-[12px]'
                  />
                </div>
              </div>
            )}

            {dateFilterType === 'month' && (
              <div className='relative w-full max-w-[200px]'>
                <Calendar className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                <input
                  type='month'
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className='w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg outline-none text-[13px]'
                />
              </div>
            )}
          </div>

          {/* Special Filter */}
          <div>
            <label className='block text-[11px] md:text-xs font-medium text-gray-700 mb-1.5'>
              Product Type
            </label>
            <select
              value={showSpecialOnly ? 'Special' : 'All'}
              onChange={(e) => { setShowSpecialOnly(e.target.value === 'Special'); setCurrentPage(1); }}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
            >
              <option value='All'>All Products</option>
              <option value='Special'>Special Products</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className='flex items-center gap-2 lg:col-span-1'>
            <button
              onClick={handleSearch}
              className='flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-medium text-[13px]'
            >
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className='flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium text-[13px]'
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* VP Summary Dashboard */}
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 animate-in fade-in slide-in-from-top-4 duration-500'>
        <div
          onClick={() => navigate('/admin/inventory-history', { state: { type: 'purchase', fromDate, toDate } })}
          className='bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]'
        >
          <div className='flex items-center gap-2 text-green-600 mb-2'>
            <ShoppingBag className='w-4 h-4' />
            <span className='text-[10px] font-bold uppercase tracking-wider'>Purchase VP</span>
          </div>
          <div className='text-xl md:text-2xl font-bold text-gray-900'>
            {vpSummary.purchase.toFixed(2)}
          </div>
          <div className='text-[10px] text-gray-500 mt-1 italic'>Click to view history</div>
        </div>

        <div
          onClick={() => navigate('/admin/inventory-history', { state: { type: 'clubUsed', fromDate, toDate } })}
          className='bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]'
        >
          <div className='flex items-center gap-2 text-blue-600 mb-2'>
            <Users className='w-4 h-4' />
            <span className='text-[10px] font-bold uppercase tracking-wider'>Club Use VP</span>
          </div>
          <div className='text-xl md:text-2xl font-bold text-gray-900'>
            {vpSummary.clubUsed.toFixed(2)}
          </div>
          <div className='text-[10px] text-gray-500 mt-1'>Click to view history</div>
        </div>

        <div
          onClick={() => navigate('/admin/sales-report', { state: { fromDate, toDate } })}
          className='bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]'
        >
          <div className='flex items-center gap-2 text-purple-600 mb-2'>
            <TrendingUp className='w-4 h-4' />
            <span className='text-[10px] font-bold uppercase tracking-wider'>Sales VP</span>
          </div>
          <div className='text-xl md:text-2xl font-bold text-gray-900'>
            {vpSummary.sale.toFixed(2)}
          </div>
          <div className='text-[10px] text-gray-500 mt-1'>Click to view history</div>
        </div>

        <div
          onClick={() => navigate('/admin/inventory-history', { state: { type: 'personalUsed', fromDate, toDate } })}
          className='bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]'
        >
          <div className='flex items-center gap-2 text-gray-600 mb-2'>
            <User className='w-4 h-4' />
            <span className='text-[10px] font-bold uppercase tracking-wider'>Personal VP</span>
          </div>
          <div className='text-xl md:text-2xl font-bold text-gray-900'>
            {vpSummary.personalUsed.toFixed(2)}
          </div>
          <div className='text-[10px] text-gray-500 mt-1'>Click to view history</div>
        </div>

        <div
          onClick={() => navigate('/admin/inventory-history', { state: { type: 'gifted', fromDate, toDate } })}
          className='bg-white p-4 rounded-2xl shadow-sm border border-gray-200 flex flex-col justify-between hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]'
        >
          <div className='flex items-center gap-2 text-orange-600 mb-2'>
            <Gift className='w-4 h-4' />
            <span className='text-[10px] font-bold uppercase tracking-wider'>Gifted VP</span>
          </div>
          <div className='text-xl md:text-2xl font-bold text-gray-900'>
            {vpSummary.gifted.toFixed(2)}
          </div>
          <div className='text-[10px] text-gray-500 mt-1'>Click to view history</div>
        </div>

        <div
          onClick={() => navigate('/admin/inventory-history', { state: { fromDate, toDate } })}
          className='bg-neutral-900 p-4 rounded-2xl shadow-lg border border-neutral-800 flex flex-col justify-between transition-all hover:scale-[1.02] cursor-pointer hover:bg-neutral-800 shadow-neutral-900/20'
        >
          <div className='flex items-center gap-2 text-neutral-400 mb-2'>
            <DollarSign className='w-4 h-4' />
            <span className='text-[10px] font-bold uppercase tracking-wider text-neutral-300'>Total Outward VP</span>
          </div>
          <div className='text-xl md:text-2xl font-bold text-white'>
            {vpSummary.totalOutward.toFixed(2)}
          </div>
          <div className='text-[10px] text-neutral-500 mt-1 font-medium'>Click to view all</div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className='hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='bg-gray-50 border-b border-gray-200'>
              <tr>
                <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Product Code
                </th>
                <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Name
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-blue-50'>
                  Opening (A)
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Purchase (B)
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Club Used
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Sale
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Personal Used
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-orange-50'>
                  Gifted
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-blue-50'>
                  Closing Stock
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-yellow-50'>
                  Purchase Price
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-green-50'>
                  Order VP
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-orange-50'>
                  Outward VP
                </th>
                <th className='px-4 py-3 text-center text-[11px] font-semibold text-gray-700 uppercase tracking-wider bg-purple-50'>
                  Available VP
                </th>
                <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-4 py-3 text-left text-[11px] font-semibold text-gray-700 uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {loading ? (
                <tr>
                  <td colSpan='13' className='px-6 py-12 text-center'>
                    <div className='flex justify-center items-center'>
                      <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
                    </div>
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan='15' className='px-6 py-12 text-center text-gray-500 text-sm'>
                    No products found
                  </td>
                </tr>
              ) : (
                paginatedProducts.map((product) => (
                  <tr key={product.id} className='hover:bg-gray-50 transition-colors'>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-[13px] font-medium text-gray-900'>{product.productCode}</div>
                    </td>
                    <td className='px-4 py-4'>
                      <div className='flex items-center gap-2'>
                        <div className='text-[13px] font-medium text-gray-900'>{product.name}</div>
                        {product.isSpecial && (
                          <span className='px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-md border border-amber-200'>
                            Special
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='px-4 py-4 text-center bg-blue-50'>
                      <div className='text-[13px] text-gray-900'>{product.stockCount || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] text-green-600 font-medium'>{product.purchase || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] text-gray-900'>{product.clubUsed || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] text-gray-900'>{product.sale || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] text-gray-900'>{product.personalUsed || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-orange-50'>
                      <div className='text-[13px] text-orange-600 font-medium'>{product.gifted || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-blue-50'>
                      <div className='text-[13px] font-bold text-gray-900'>{calculateClosingStock(product)}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-yellow-50'>
                      <div className='text-[13px] text-gray-900'>₹{product.purchasePrice || 0}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-green-50'>
                      <div className='text-[13px] font-bold text-green-700'>{calculateOrderVP(product)}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-orange-50'>
                      <div className='text-[13px] font-bold text-orange-700'>{calculateOutwardVP(product)}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-purple-50'>
                      <div className='text-[13px] font-bold text-purple-700'>{calculateAvailableVP(product)}</div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <span
                        className={`px-3 py-1 inline-flex text-[11px] leading-5 font-semibold rounded-full ${product.status === 'active' || product.isActive === true
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {product.status === 'active' || product.isActive === true ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap text-sm'>
                      <div className='flex gap-2'>
                        <button
                          onClick={() => navigate('/admin/product/add', { state: { cloneData: product } })}
                          className='p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors'
                          title='Clone'
                        >
                          <Copy className='w-4 h-4' />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                          className='p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors'
                          title='Edit'
                        >
                          <Edit className='w-4 h-4' />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                          title='Delete'
                        >
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {/* Totals Row */}
              {!loading && filteredProducts.length > 0 && (() => {
                const totals = filteredProducts.reduce((acc, product) => ({
                  opening: acc.opening + Number(product.stockCount || 0),
                  purchase: acc.purchase + Number(product.purchase || 0),
                  clubUsed: acc.clubUsed + Number(product.clubUsed || 0),
                  sale: acc.sale + Number(product.sale || 0),
                  personalUsed: acc.personalUsed + Number(product.personalUsed || 0),
                  gifted: acc.gifted + Number(product.gifted || 0),
                  closingStock: acc.closingStock + calculateClosingStock(product),
                  orderVP: acc.orderVP + calculateOrderVP(product),
                  outwardVP: acc.outwardVP + calculateOutwardVP(product),
                  availableVP: acc.availableVP + calculateAvailableVP(product),
                }), {
                  opening: 0,
                  purchase: 0,
                  clubUsed: 0,
                  sale: 0,
                  personalUsed: 0,
                  gifted: 0,
                  closingStock: 0,
                  orderVP: 0,
                  outwardVP: 0,
                  availableVP: 0,
                });

                return (
                  <tr className='bg-gray-100 border-t-2 border-gray-400 font-bold'>
                    <td className='px-4 py-4 whitespace-nowrap' colSpan='2'>
                      <div className='text-[13px] font-bold text-gray-900'>TOTAL</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-blue-100'>
                      <div className='text-[13px] font-bold text-gray-900'>{totals.opening}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] font-bold text-green-600'>{totals.purchase}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] font-bold text-gray-900'>{totals.clubUsed}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] font-bold text-gray-900'>{totals.sale}</div>
                    </td>
                    <td className='px-4 py-4 text-center'>
                      <div className='text-[13px] font-bold text-gray-900'>{totals.personalUsed}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-orange-100'>
                      <div className='text-[13px] font-bold text-orange-700'>{totals.gifted}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-blue-100'>
                      <div className='text-[13px] font-bold text-blue-700'>{totals.closingStock}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-yellow-100'>
                      {/* Purchase Price - no meaningful total */}
                      <div className='text-[13px] font-bold text-gray-400'>—</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-green-100'>
                      <div className='text-[13px] font-bold text-green-700'>{totals.orderVP.toFixed(2)}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-orange-100'>
                      <div className='text-[13px] font-bold text-orange-700'>{totals.outwardVP.toFixed(2)}</div>
                    </td>
                    <td className='px-4 py-4 text-center bg-purple-100'>
                      <div className='text-[13px] font-bold text-purple-700'>{totals.availableVP.toFixed(2)}</div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap' colSpan='2'>
                      <span className='px-3 py-1 inline-flex text-[11px] leading-5 font-semibold rounded-full bg-gray-200 text-gray-800'>
                        All Products
                      </span>
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop Pagination */}
      {!loading && filteredProducts.length > 0 && (
        <div className='hidden lg:flex items-center justify-between bg-white rounded-2xl shadow-sm border border-gray-200 px-5 py-3 mt-3'>
          <p className='text-[12px] text-gray-600'>
            Showing <span className='font-semibold'>{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>–<span className='font-semibold'>{Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)}</span> of <span className='font-semibold'>{filteredProducts.length}</span> products
          </p>
          <div className='flex items-center gap-1'>
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
              className='px-2 py-1 text-[12px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>«</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className='px-3 py-1 text-[12px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, i) => p === '...' ? (
                <span key={`el-${i}`} className='px-2 text-gray-400 text-[12px]'>...</span>
              ) : (
                <button key={p} onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1 text-[12px] rounded-lg border transition ${currentPage === p ? 'bg-neutral-900 text-white border-neutral-900 font-semibold' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{p}</button>
              ))}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className='px-3 py-1 text-[12px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>Next ›</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
              className='px-2 py-1 text-[12px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>»</button>
          </div>
        </div>
      )}

      {/* Mobile Cards */}
      <div className='lg:hidden space-y-3 md:space-y-4'>
        {loading ? (
          <div className='flex justify-center items-center py-12'>
            <div className='w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center text-gray-500 text-[13px]'>
            No products found
          </div>
        ) : (
          paginatedProducts.map((product) => (
            <div
              key={product.id}
              className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4'
            >
              <div className='flex justify-between items-start mb-3'>
                <div className='flex-1 min-w-0'>
                  <h3 className='text-[13px] md:text-base font-semibold text-gray-900 truncate'>
                    {product.name}
                  </h3>
                  <p className='text-[11px] md:text-sm text-gray-500 mt-1'>
                    {product.productCode}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-full flex-shrink-0 ml-2 ${product.status === 'active' || product.isActive === true
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}
                >
                  {product.status === 'active' || product.isActive === true ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className='space-y-2 mb-4'>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Opening Stock:</span>
                  <span className='text-[11px] md:text-sm text-gray-600'>{product.stockCount || 0}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Purchase Price:</span>
                  <span className='text-[11px] md:text-sm text-gray-600'>₹{product.purchasePrice || 0}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Purchase:</span>
                  <span className='text-[11px] md:text-sm text-green-600 font-medium'>{product.purchase || 0}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Club Used:</span>
                  <span className='text-[11px] md:text-sm text-gray-600'>{product.clubUsed || 0}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Sale:</span>
                  <span className='text-[11px] md:text-sm text-gray-600'>{product.sale || 0}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Personal Used:</span>
                  <span className='text-[11px] md:text-sm text-gray-600'>{product.personalUsed || 0}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-medium text-gray-700'>Gifted:</span>
                  <span className='text-[11px] md:text-sm text-orange-600 font-medium'>{product.gifted || 0}</span>
                </div>
                <div className='flex justify-between pt-2 border-t border-gray-200'>
                  <span className='text-[11px] md:text-sm font-bold text-gray-700'>Closing Stock:</span>
                  <span className='text-[11px] md:text-sm font-bold text-gray-900'>{calculateClosingStock(product)}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-bold text-green-700'>Order VP:</span>
                  <span className='text-[11px] md:text-sm font-bold text-green-700'>{calculateOrderVP(product)}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-bold text-orange-700'>Outward VP:</span>
                  <span className='text-[11px] md:text-sm font-bold text-orange-700'>{calculateOutwardVP(product)}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-[11px] md:text-sm font-bold text-purple-700'>Available VP:</span>
                  <span className='text-[11px] md:text-sm font-bold text-purple-700'>{calculateAvailableVP(product)}</span>
                </div>
              </div>
              <div className='flex gap-2 pt-3 border-t border-gray-200'>
                <button
                  onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                  className='flex items-center gap-2 px-3 md:px-4 h-9 text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors flex-1 justify-center font-medium text-[11px] md:text-sm'
                >
                  <Edit className='w-4 h-4' />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className='flex items-center gap-2 px-3 md:px-4 h-9 text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors flex-1 justify-center font-medium text-[11px] md:text-sm'
                >
                  <Trash2 className='w-4 h-4' />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Mobile Pagination */}
      {!loading && filteredProducts.length > 0 && (
        <div className='lg:hidden flex items-center justify-between bg-white rounded-2xl shadow-sm border border-gray-200 px-4 py-3 mt-3'>
          <p className='text-[11px] text-gray-600'>
            {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)} / {filteredProducts.length}
          </p>
          <div className='flex items-center gap-2'>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className='px-4 py-1.5 text-[12px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium'>‹ Prev</button>
            <span className='text-[12px] text-gray-600 font-medium'>{currentPage} / {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className='px-4 py-1.5 text-[12px] rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium'>Next ›</button>
          </div>
        </div>
      )}
    </div >
  );
};

export default Products;
